import { createContext, useState, useLayoutEffect } from "react";

export const variableContext = createContext();

// ── Layout Aliases ──────────────────────────────────────────────────────────
//
// The layoutMode state uses two internal string values: "grid" and "list".
// These are the official human-readable aliases for use in comments and
// future code. Do NOT change the string values — they are persisted in
// localStorage and used throughout the project.
//
//   Layout 1  =  layoutMode === "grid"   (switch thumb on the LEFT)
//   Layout 2  =  layoutMode === "list"   (switch thumb on the RIGHT)
//
export const LAYOUT_1 = "grid";   // Layout 1 — original grid/card view
export const LAYOUT_2 = "list";   // Layout 2 — compact list view
// ────────────────────────────────────────────────────────────────────────────

const Variable = ({ children }) => {
  const [mobile, setMobile] = useState("");

  const [selectedCategories, setSelectedCategories] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('selectedCategories')) || [];
    } catch { return []; }
  });

  const toggleSelectedCategory = (category) => {
    setSelectedCategories(prev => {
      const exists = prev.some(c => c.id === category.id);
      const updated = exists
        ? prev.filter(c => c.id !== category.id)
        : [...prev, category];
      sessionStorage.setItem('selectedCategories', JSON.stringify(updated));
      return updated;
    });
  };

  const [layoutMode, setLayoutMode] = useState(() => {
    const saved = localStorage.getItem("pd-layout");
    // Default to "grid" unless user explicitly saved "list"
    return saved === "list" ? "list" : "grid";
  });

  const setLayoutModeAndPersist = (mode) => {
    localStorage.setItem("pd-layout", mode);
    setLayoutMode(mode);
  };

  const [locationData, setLocationData] = useState(() => {
    const savedFilters = JSON.parse(sessionStorage.getItem('locationData')) || {};
    const defaultRadius = 200;
    const initialRadius = savedFilters.radius ?? defaultRadius;
    const initialFilters = { radius: initialRadius, ...savedFilters };

    if (savedFilters.radius === undefined) {
      sessionStorage.setItem('locationData', JSON.stringify(initialFilters));
    }

    return initialFilters;
  });

  const setLocationFilter = (field, data) => {
    setLocationData((prevData) => {
      // Update the specified field with the new data
      const updatedFilters = { ...prevData, [field]: data };

      // Save updated filters to sessionStorage
      sessionStorage.setItem('locationData', JSON.stringify(updatedFilters));

      return updatedFilters;
    });
  };

  // ── Permanent layout mapping ──────────────────────────────────────────────
  // Switch LEFT  = layoutMode "grid"  = Layout 1  → body[data-layout1="true"]
  // Switch RIGHT = layoutMode "list"  = Layout 2  → body[data-layout2="true"]
  // These are the ONLY two body attributes used for CSS layout scoping.
  // Never invert this logic. Never add a third attribute.
  // ─────────────────────────────────────────────────────────────────────────
  useLayoutEffect(() => {
    if (layoutMode === "grid") {
      document.body.setAttribute("data-layout1", "true");
      document.body.removeAttribute("data-layout2");
    } else {
      document.body.setAttribute("data-layout2", "true");
      document.body.removeAttribute("data-layout1");
    }
  }, [layoutMode]);

  return (
    <variableContext.Provider
      value={{
        mobile,
        setMobile,
        locationData,
        setLocationFilter,
        layoutMode,
        setLayoutMode: setLayoutModeAndPersist,
        selectedCategories,
        toggleSelectedCategory,
      }}
    >
      {children}
    </variableContext.Provider>
  );
};

export default Variable;
